//#region importsSection
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Spinner,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { GetLanguageString } from "../helper/Components";
import swal from "sweetalert";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import { ApiDocuments } from "../documents/ApiDocuments";
import { ApiFinance } from "../funds/ApiFinance";
import { LoadingSpinner, applyClassToCharacters } from "../../constants/const";
//#endregion

//#region styles
const labelStyle = {
  fontSize: "10.5px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#9CA3AF",
  marginBottom: "2px",
  lineHeight: "1.2",
};

const valueStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#1F2937",
  lineHeight: "1.3",
};

const cardBase = {
  borderRadius: "10px",
  border: "1px solid #E5E7EB",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  marginBottom: "10px",
};

const sectionHeader = (color) => ({
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.05em",
  color,
  textTransform: "uppercase",
  marginBottom: "8px",
});

const animatedBody = {
  animation: "fadeSlideIn 0.25s ease-out",
  padding: "14px 18px",
};

const amountBadge = (bgColor = "#D1FAE5", textColor = "#059669") => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "10px",
  backgroundColor: bgColor,
  color: textColor,
  fontWeight: 600,
  fontSize: "13px",
});
//#endregion

const TransactionTransferNotificationModal = ({
  setViewFinanceNotificationModel,
  notificationId,
}) => {
  const [notificationresponse, setNotificationresponse] = useState({});
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const toggle = () => setViewFinanceNotificationModel(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const handleDrag = (e, ui) =>
    setPosition((p) => ({ x: p.x + ui.deltaX, y: p.y + ui.deltaY }));
  const [viewButtonDisable, setViewButtonDisable] = useState(
    Array(notificationresponse.expenses?.length).fill(false)
  );
  const [buttonDisable, setButtonDisable] = useState(
    Array(notificationresponse.expenses?.length).fill(false)
  );
  useEffect(() => {
    if (!notificationId) return;
    setLoading(true);
    ApiFinance()
      .GetTransferDataForNotification(notificationId)
      .then((res) => setNotificationresponse(res.data || {}))
      .finally(() => setLoading(false));
  }, [notificationId]);

  const openFile = async (fileUrl, index) => {
    setViewButtonDisable((prevStates) => {
      const newLoadingStates = [...prevStates];
      newLoadingStates[index] = true;
      return newLoadingStates;
    });

    try {
      if (fileUrl) {
        window.open(fileUrl, "_blank");
      } else if (notificationresponse.transactionId) {
        const res = await ApiFinance().ViewTransactionAttachment(notificationresponse.transactionId);
        const contentType = (res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) || "application/octet-stream";
        const blob = new Blob([res.data], { type: contentType });
        const objUrl = window.URL.createObjectURL(blob);
        window.open(objUrl, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(objUrl), 100);
      }
    } catch (err) {
      swal({
        title: "Error opening file",
        icon: "error",
        buttons: "OK",
      });
      console.error(err);
    } finally {
      setViewButtonDisable((prevStates) => {
        const newLoadingStates = [...prevStates];
        newLoadingStates[index] = false;
        return newLoadingStates;
      });
    }
  };

  const downloadFile = async (fileId, index) => {
    setButtonDisable((prevStates) => {
      const newStates = [...prevStates];
      newStates[index] = true;
      return newStates;
    });

    try {
      if (fileId) {
        const response = await ApiDocuments().downloadDocument(fileId);
        if (response.status === 200) {
          const modifiedLink = `${response.data.downloadLink}&confirm=t`;
          window.location.href = modifiedLink;
        } else {
          swal({
            title: t(response.data),
            icon: "error",
            buttons: "OK",
          });
          console.error("Error getting download link:", response.data);
        }
      } else if (notificationresponse.transactionId) {
        const res = await ApiFinance().ViewTransactionAttachment(notificationresponse.transactionId);
        const contentType = (res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) || "application/octet-stream";
        const blob = new Blob([res.data], { type: contentType });
        const objUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `TransferAttachment_${notificationresponse.transactionId}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(objUrl), 100);
      }
    } catch (error) {
      swal({
        title: error.message,
        icon: "error",
        buttons: "OK",
      });
    } finally {
      setButtonDisable((prevStates) => {
        const newStates = [...prevStates];
        newStates[index] = false;
        return newStates;
      });
    }
  };

  const orig = notificationresponse.original;
  const prop = notificationresponse;

  const formatDisplayDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-GB") : "";
  const formatDisplayAmount = (a) =>
    new Intl.NumberFormat().format(Number(a) || 0);

  const isDateDiff =
    orig &&
    formatDisplayDate(orig.requestDate) !== formatDisplayDate(prop.requestDate);
  const isAmountDiff =
    orig && Number(orig.transferAmount || 0) !== Number(prop.transferAmount || 0);
  const isBankChargeDiff =
    orig && Number(orig.bankCharge || 0) !== Number(prop.bankCharge || 0);
  const isReferenceDiff =
    orig && (orig.referenceNo || "").trim() !== (prop.referenceNo || "").trim();
  const isMemoDiff =
    orig && (orig.memo || "").trim() !== (prop.memo || "").trim();

  const hasDifferences =
    isDateDiff ||
    isAmountDiff ||
    isBankChargeDiff ||
    isReferenceDiff ||
    isMemoDiff;

  return (
    <>
      {/* animation keyframes */}
      <style>
        {`
          @keyframes fadeSlideIn {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <Draggable position={position} onDrag={handleDrag} handle=".modal-header" cancel=".no-drag">
        <Modal size="xl" isOpen className="inmodal">
          <ModalHeader className="modal-title">
            <GetLanguageString props="common_changes" />
          </ModalHeader>

          {loading ? (
            <ModalBody className="text-center py-4">
              <LoadingSpinner />
            </ModalBody>
          ) : (
            <ModalBody style={animatedBody}>
              {orig ? (
                <>
                  {/* TOP COMMON DETAILS CARD */}
                  <Card
                    className="mb-3"
                    style={{
                      ...cardBase,
                      borderLeft: "5px solid #3B82F6",
                    }}
                  >
                    <CardBody style={{ padding: "16px 20px" }}>
                      <div style={sectionHeader("#2563EB")}>
                        <i className="fa fa-info-circle" style={{ marginRight: "6px" }}></i>
                        Transfer Information
                      </div>
                      <Row className="gy-3">
                        <Col md="4">
                          <div style={labelStyle}>
                            <GetLanguageString props="dashboard_event_page_unit_name" />
                          </div>
                          <div className="ur" style={valueStyle}>
                            {prop.unitName}
                          </div>
                        </Col>
                        <Col md="4">
                          <div style={labelStyle}>
                            <GetLanguageString props="member_report_from" />
                          </div>
                          <div style={valueStyle}>
                            {prop.fromAccount}
                          </div>
                        </Col>
                        <Col md="4">
                          <div style={labelStyle}>
                            <GetLanguageString props="member_report_to" />
                          </div>
                          <div style={valueStyle}>
                            {prop.toAccount}
                          </div>
                        </Col>
                        {!isAmountDiff && (
                          <Col md="4">
                            <div style={labelStyle}>
                              <GetLanguageString props="funds_add_fund_amount" />
                            </div>
                            <div style={amountBadge("#D1FAE5", "#059669")}>
                              {formatDisplayAmount(prop.transferAmount)}
                            </div>
                          </Col>
                        )}
                        {!isDateDiff && prop.requestDate && (
                          <Col md="4">
                            <div style={labelStyle}>
                              <GetLanguageString props="dashboard_event_page_date" />
                            </div>
                            <div style={valueStyle}>
                              {formatDisplayDate(prop.requestDate)}
                            </div>
                          </Col>
                        )}
                        {!isBankChargeDiff && prop.bankCharge !== undefined && prop.bankCharge !== null && (
                          <Col md="4">
                            <div style={labelStyle}>
                              <GetLanguageString props="transactions_bank_charges" />
                            </div>
                            <div style={valueStyle}>
                              {formatDisplayAmount(prop.bankCharge)}
                            </div>
                          </Col>
                        )}
                        {!isReferenceDiff && prop.referenceNo && (
                          <Col md="4">
                            <div style={labelStyle}>
                              <GetLanguageString props="transactions_bank_deposit_slip_number" />
                            </div>
                            <div style={valueStyle}>
                              {prop.referenceNo}
                            </div>
                          </Col>
                        )}
                        {!isMemoDiff && prop.memo && (
                          <Col md="12">
                            <div style={labelStyle}>
                              <GetLanguageString props="transactions_memo" />
                            </div>
                            <div style={valueStyle}>
                              {prop.memo}
                            </div>
                          </Col>
                        )}
                      </Row>
                    </CardBody>
                  </Card>

                  {/* SIDE-BY-SIDE COMPARISON CARDS */}
                  {hasDifferences && (
                    <Row className="mb-3">
                      {/* ORIGINAL DETAILS */}
                      <Col md="6">
                        <Card
                          className="h-100 mb-0"
                          style={{
                            ...cardBase,
                            background: "#FEF2F2",
                            borderLeft: "5px solid #EF4444",
                          }}
                        >
                          <CardBody style={{ padding: "16px 20px" }}>
                            <div style={sectionHeader("#DC2626")}>
                              <i className="fa fa-history" style={{ marginRight: "6px" }}></i>
                              <GetLanguageString props="notification_original_details" />
                            </div>
                            <Row className="gy-3">
                              {isAmountDiff && (
                                <Col md="6">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="funds_add_fund_amount" />
                                  </div>
                                  <div style={amountBadge("#FEE2E2", "#DC2626")}>
                                    {formatDisplayAmount(orig.transferAmount)}
                                  </div>
                                </Col>
                              )}
                              {isDateDiff && (
                                <Col md="6">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="dashboard_event_page_date" />
                                  </div>
                                  <div style={valueStyle}>
                                    {formatDisplayDate(orig.requestDate) || "—"}
                                  </div>
                                </Col>
                              )}
                              {isBankChargeDiff && (
                                <Col md="6">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="transactions_bank_charges" />
                                  </div>
                                  <div style={valueStyle}>
                                    {formatDisplayAmount(orig.bankCharge)}
                                  </div>
                                </Col>
                              )}
                              {isReferenceDiff && (
                                <Col md="12">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="transactions_bank_deposit_slip_number" />
                                  </div>
                                  <div style={valueStyle}>
                                    {orig.referenceNo || "—"}
                                  </div>
                                </Col>
                              )}
                              {isMemoDiff && (
                                <Col md="12">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="transactions_memo" />
                                  </div>
                                  <div style={valueStyle}>
                                    {orig.memo || "—"}
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </CardBody>
                        </Card>
                      </Col>

                      {/* PROPOSED DETAILS */}
                      <Col md="6">
                        <Card
                          className="h-100 mb-0"
                          style={{
                            ...cardBase,
                            background: "#ECFDF5",
                            borderLeft: "5px solid #10B981",
                          }}
                        >
                          <CardBody style={{ padding: "16px 20px" }}>
                            <div style={sectionHeader("#059669")}>
                              <i className="fa fa-edit" style={{ marginRight: "6px" }}></i>
                              <GetLanguageString props="notification_proposed_changes" />
                            </div>
                            <Row className="gy-3">
                              {isAmountDiff && (
                                <Col md="6">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="funds_add_fund_amount" />
                                  </div>
                                  <div style={amountBadge("#D1FAE5", "#059669")}>
                                    {formatDisplayAmount(prop.transferAmount)}
                                  </div>
                                </Col>
                              )}
                              {isDateDiff && (
                                <Col md="6">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="dashboard_event_page_date" />
                                  </div>
                                  <div style={valueStyle}>
                                    {formatDisplayDate(prop.requestDate) || "—"}
                                  </div>
                                </Col>
                              )}
                              {isBankChargeDiff && (
                                <Col md="6">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="transactions_bank_charges" />
                                  </div>
                                  <div style={valueStyle}>
                                    {formatDisplayAmount(prop.bankCharge)}
                                  </div>
                                </Col>
                              )}
                              {isReferenceDiff && (
                                <Col md="12">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="transactions_bank_deposit_slip_number" />
                                  </div>
                                  <div style={valueStyle}>
                                    {prop.referenceNo || "—"}
                                  </div>
                                </Col>
                              )}
                              {isMemoDiff && (
                                <Col md="12">
                                  <div style={labelStyle}>
                                    <GetLanguageString props="transactions_memo" />
                                  </div>
                                  <div style={valueStyle}>
                                    {prop.memo || "—"}
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  )}
                </>
              ) : (
                /* NEW TRANSFER (NON-MODIFICATION) INFO */
                <Card
                  className="mb-3"
                  style={{
                    ...cardBase,
                    background: "#ECFDF5",
                    borderLeft: "5px solid #10B981",
                  }}
                >
                  <CardBody style={{ padding: "16px 20px" }}>
                    <div style={sectionHeader("#059669")}>
                      Transfer Information
                    </div>
                    <Row className="gy-3">
                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="dashboard_event_page_unit_name" />
                        </div>
                        <div className="ur" style={valueStyle}>
                          {notificationresponse.unitName}
                        </div>
                      </Col>
                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="member_report_from" />
                        </div>
                        <div style={valueStyle}>
                          {notificationresponse.fromAccount}
                        </div>
                      </Col>

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="member_report_to" />
                        </div>
                        <div style={valueStyle}>
                          {notificationresponse.toAccount}
                        </div>
                      </Col>
                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="funds_add_fund_amount" />
                        </div>
                        <div style={amountBadge("#D1FAE5", "#059669")}>
                          {new Intl.NumberFormat().format(
                            notificationresponse.transferAmount || 0
                          )}
                        </div>
                      </Col>

                      {notificationresponse.bankCharge !== undefined &&
                        notificationresponse.bankCharge !== null && (
                          <Col md="4">
                            <div style={labelStyle}>
                              <GetLanguageString props="transactions_bank_charges" />
                            </div>
                            <div style={valueStyle}>
                              {new Intl.NumberFormat().format(
                                notificationresponse.bankCharge || 0
                              )}
                            </div>
                          </Col>
                        )}

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="dashboard_event_page_date" />
                        </div>
                        <div style={valueStyle}>
                          {notificationresponse.requestDate
                            ? new Date(
                                notificationresponse.requestDate
                              ).toLocaleDateString("en-GB")
                            : ""}
                        </div>
                      </Col>
                      {notificationresponse.referenceNo && (
                        <Col md="4">
                          <div style={labelStyle}>
                            <GetLanguageString props="transactions_bank_deposit_slip_number" />
                          </div>
                          <div style={valueStyle}>
                            {notificationresponse.referenceNo}
                          </div>
                        </Col>
                      )}
                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_memo" />
                        </div>
                        <div style={valueStyle}>{notificationresponse.memo}</div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}

              {/* BANK INFO */}
              {(notificationresponse.rafiqAccountTitle ||
                notificationresponse.rafiqAccountNumber ||
                notificationresponse.rafiqBankName ||
                notificationresponse.cnic) && (
                <Card
                  className="mb-3"
                  style={{
                    ...cardBase,
                    background: "#EFF6FF",
                    borderLeft: "5px solid #3B82F6",
                  }}
                >
                  <CardBody style={{ padding: "12px 18px" }}>
                    <div style={sectionHeader("#2563EB")}>
                      <GetLanguageString props="transactions_beneficiary_details" />
                    </div>

                    <Row className="gy-2">
                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_account_title" />
                        </div>
                        <div style={valueStyle}>
                          {applyClassToCharacters(
                            notificationresponse.rafiqAccountTitle,
                            "ur"
                          )}
                        </div>
                      </Col>

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_account_number" />
                        </div>
                        <div style={valueStyle}>
                          {applyClassToCharacters(
                            notificationresponse.rafiqAccountNumber,
                            "ur"
                          )}
                        </div>
                      </Col>

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_bank_name" />
                        </div>
                        <div style={valueStyle}>
                          {applyClassToCharacters(
                            notificationresponse.rafiqBankName,
                            "ur"
                          )}
                        </div>
                      </Col>

                      {notificationresponse.cnic && (
                        <Col md="4">
                          <div style={labelStyle}>
                            <GetLanguageString props="member_cnic" />
                          </div>
                          <div style={valueStyle}>
                            {applyClassToCharacters(
                              (() => {
                                const rawCnic = String(notificationresponse.cnic).replace(/\D/g, "");
                                return rawCnic.length === 13
                                  ? `${rawCnic.slice(0, 5)}-${rawCnic.slice(5, 12)}-${rawCnic.slice(12)}`
                                  : notificationresponse.cnic;
                              })(),
                              "ur"
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </CardBody>
                </Card>
              )}

              {/* ATTACHMENT */}
              {(notificationresponse.depositSlipFile ||
                notificationresponse.shuraApprovalFile ||
                notificationresponse.fileId ||
                notificationresponse.file_id ||
                (notificationresponse.isApproved && notificationresponse.transactionId)) && (
                <Card
                  className="mb-3"
                  style={{
                    ...cardBase,
                    background: "#F0F9FF",
                    borderLeft: "5px solid #0EA5E9",
                  }}
                >
                  <CardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <div style={sectionHeader("#0284C7")}>
                        <GetLanguageString props="attachment" />
                      </div>
                      <div style={valueStyle}>
                        <GetLanguageString props="deposit_slip" />
                      </div>
                    </div>

                    <div className="no-drag">
                      {(() => {
                        const shuraIndex = notificationresponse.expenses
                          ? notificationresponse.expenses.length
                          : 0;
                        const attachmentUrl =
                          notificationresponse.depositSlipFile ||
                          notificationresponse.shuraApprovalFile;
                        const attachmentDriveId =
                          notificationresponse.depositSlipFileDriveId ||
                          notificationresponse.shuraApprovalFileDriveId;
                        return (
                          <>
                            <Button
                              color="white"
                              disabled={viewButtonDisable[shuraIndex]}
                              size="sm"
                              onClick={() =>
                                openFile(attachmentUrl, shuraIndex)
                              }
                            >
                              {viewButtonDisable[shuraIndex] ? (
                                <Spinner size="sm" color="dark" />
                              ) : (
                                <>
                                  <i className="fa fa-eye"></i>{" "}
                                  <GetLanguageString props="member_add_rafiq_request_view_document" />
                                </>
                              )}
                            </Button>{" "}
                            <Button
                              color="white"
                              size="sm"
                              disabled={buttonDisable[shuraIndex]}
                              onClick={() =>
                                downloadFile(attachmentDriveId, shuraIndex)
                              }
                            >
                              {buttonDisable[shuraIndex] ? (
                                <Spinner size="sm" color="dark" />
                              ) : (
                                <>
                                  <i className="fa fa-download"></i>{" "}
                                  <GetLanguageString props="member_add_rafiq_request_download_document" />
                                </>
                              )}
                            </Button>
                          </>
                        );
                      })()}
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* EXPENSES */}
              {notificationresponse.expenses?.length > 0 && (
                <Card style={cardBase}>
                  <CardBody>
                    <div style={sectionHeader("#2563EB")}>
                      <GetLanguageString props="sidebar_funds_expenses" />
                    </div>

                    <Table responsive className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>
                            <GetLanguageString props="sidebar_funds_expense" />
                          </th>
                          <th>
                            <GetLanguageString props="funds_add_fund_amount" />
                          </th>
                          <th>
                            <GetLanguageString props="dashboard_event_page_date" />
                          </th>
                          <th>
                            <GetLanguageString props="transactions_expense_type" />
                          </th>
                          <th className="text-center">
                            <GetLanguageString props="transactions_expense_receipt" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {notificationresponse.expenses.map((x, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{x.description}</td>
                            <td>
                              <span style={amountBadge("#DBEAFE", "#2563EB")}>
                                {x.amount}
                              </span>
                            </td>
                            <td>
                              {notificationresponse.requestDate
                                ? new Date(x.date).toLocaleDateString("en-GB")
                                : ""}
                            </td>

                            <td>
                              {x.expenseType
                                ? x.expenseType.charAt(0).toUpperCase() +
                                  x.expenseType.slice(1)
                                : ""}
                            </td>

                            <td className="text-center">
                              {x.expenseFile && (
                                <>
                                  <Button
                                    color="white"
                                    disabled={viewButtonDisable[i]}
                                    size="sm"
                                    onClick={() => openFile(x.expenseFile, i)}
                                  >
                                    {viewButtonDisable[i] ? (
                                      <Spinner size="sm" color="dark" />
                                    ) : (
                                      <>
                                        <i class="fa fa-eye"></i>{" "}
                                        <GetLanguageString props="member_add_rafiq_request_view_document" />
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    color="white"
                                    size="sm"
                                    disabled={buttonDisable[i]}
                                    onClick={() =>
                                      downloadFile(x.expenseFileDriveId, i)
                                    }
                                  >
                                    {buttonDisable[i] ? (
                                      <Spinner size="sm" color="dark" />
                                    ) : (
                                      <>
                                        <i class="fa fa-download"></i>{" "}
                                        <GetLanguageString props="member_add_rafiq_request_download_document" />
                                      </>
                                    )}
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}
            </ModalBody>
          )}

          <ModalFooter>
            <Button color="primary" onClick={toggle}>
              <GetLanguageString props="users_role_ok_btn" />
            </Button>
          </ModalFooter>
        </Modal>
      </Draggable>
    </>
  );
};

export default TransactionTransferNotificationModal;
